import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import MapView from "../components/MapView";
import WaitingListCallout from "../components/WaitingListCallout";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api, getUser, isAdminUser } from "../lib/api";
import { LOAD_SIZE_LABEL } from "./PostWaste";

// Task 6 — item-level category chips. Each chip resolves via a matcher
// that inspects the post's `waste_type` (broad category set in the
// wizard) and, for chips that don't map 1:1 (Appliances, Mattress),
// falls back to keyword matches on title + description.
const CATEGORY_CHIPS = [
  { id: "all",         label: "All",         match: () => true },
  { id: "furniture",   label: "Furniture",   match: (p) => /bulky\s*furniture|furniture|sofa|armchair|wardrobe|desk|chair|table|bed(?!\s*sheet)/i.test(`${p.waste_type} ${p.title} ${p.description || ""}`) },
  { id: "appliances",  label: "Appliances",  match: (p) => /appliance|washer|dryer|fridge|freezer|dishwasher|oven|cooker|microwave/i.test(`${p.title} ${p.description || ""}`) },
  { id: "garden",      label: "Garden",      match: (p) => /garden|green|leaves|hedge|soil|turf|branches/i.test(`${p.waste_type} ${p.title} ${p.description || ""}`) },
  { id: "builder",     label: "Builder waste", match: (p) => /construction|rubble|builder|brick|plaster|tile|concrete|renovation/i.test(`${p.waste_type} ${p.title} ${p.description || ""}`) },
  { id: "mattress",    label: "Mattress",    match: (p) => /mattress/i.test(`${p.title} ${p.description || ""}`) },
  { id: "electronics", label: "Electronics", match: (p) => /electronic|weee|tv\b|monitor|laptop|printer|battery/i.test(`${p.waste_type} ${p.title} ${p.description || ""}`) },
  { id: "mixed",       label: "Mixed/Other", match: (p) => /^mixed$|recycling|food|organic|hazardous|other/i.test(p.waste_type || "") },
];
import { currentPosition } from "../lib/native";
import { forwardGeocode, reverseGeocode } from "../lib/geocode";
import { MapPin, Search, Map as MapIcon, List, Compass, ImageIcon, Clock } from "lucide-react";

// Human-readable "Posted X ago" for card / detail timestamps. Falls back
// to an empty string if parsing fails so we never render `Invalid Date`.
// `compact=true` returns "2h ago" (marketplace card); default is the
// spelled-out "2 hours ago" (job detail).
export function timeAgo(iso, { compact = false } = {}) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return "Just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return compact ? `${mins}m ago` : `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return compact ? `${hrs}h ago` : `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return compact ? `${days}d ago` : `${days} day${days === 1 ? "" : "s"} ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return compact ? `${weeks}w ago` : `${weeks} week${weeks === 1 ? "" : "s"} ago`;
  const months = Math.floor(days / 30);
  return compact ? `${months}mo ago` : `${months} month${months === 1 ? "" : "s"} ago`;
}

// Haversine in km — same formula as the backend. Used to decide whether the
// current search location falls inside any carrier's service circle.
function distanceKm(a, b) {
  const R = 6371;
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLng = ((b[1] - a[1]) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a[0] * Math.PI) / 180) *
      Math.cos((b[0] * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export default function Marketplace() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [query, setQuery] = useState("");
  const [view, setView] = useState("split");
  const [nearby, setNearby] = useState(false);
  const [center, setCenter] = useState(null);
  const [areaLabel, setAreaLabel] = useState("");
  const [coverage, setCoverage] = useState([]);
  const [showCoverage, setShowCoverage] = useState(true);
  // Phase 2 filter chip: "all" | "remove" | "clear". Persisted only in
  // component state — resets on route re-entry, matches how `nearby` works.
  const [jobTypeFilter, setJobTypeFilter] = useState("all");
  // Task 6 — item-level category chip. Client-side matcher — the backend
  // stores broad `waste_type` values plus free-text titles/descriptions,
  // so each chip resolves to a set of waste_type strings + optional
  // keyword matches on title/description.
  const [categoryFilter, setCategoryFilter] = useState("all");
  // Imperative fly-to for the map: {lat, lng, zoom, label} — mutated by
  // the Near-me button (real coords) and by the search bar (geocoded
  // location). Nulled to yield back to auto-fit.
  const [focus, setFocus] = useState(null);
  const user = getUser();
  // Task 2 — non-admin users never see the map, so "split" (map + list)
  // is pointless for them. Coerce the effective view to "list" so the
  // grid isn't capped at the split-mode max-height (which was squashing
  // all cards down to a few pixels).
  const effectiveView = isAdminUser(user) ? view : "list";
  const viewerId = user?.id;
  // When a carrier has a service area set, the backend will filter posts to
  // within that radius — display a small status badge so they understand the scope.
  const carrierArea = user?.role === "carrier" && user?.service_area_radius_km
    ? { label: user.service_area_label, radius: user.service_area_radius_km }
    : null;

  useEffect(() => {
    if (nearby && center) {
      // 50km radius works well for both city-level geolocation and the UK-wide fallback.
      const params = { lat: center[0], lng: center[1], radius_km: 50 };
      if (jobTypeFilter !== "all") params.job_type = jobTypeFilter;
      api.get("/waste-posts/nearby/search", { params }).then((r) => setPosts(r.data));
    } else {
      const params = { status: "open", viewer_id: viewerId };
      if (jobTypeFilter !== "all") params.job_type = jobTypeFilter;
      api.get("/waste-posts", { params }).then((r) => setPosts(r.data));
    }
  }, [nearby, center, viewerId, jobTypeFilter]);

  // Carrier coverage is orthogonal to the search — load once on mount.
  useEffect(() => {
    api.get("/marketplace/coverage").then((r) => setCoverage(r.data)).catch(() => setCoverage([]));
  }, []);

  const enableNearby = () => {
    // Route through the cross-platform helper: on native iOS/Android this
    // uses the Capacitor Geolocation plugin (which triggers the proper
    // CLLocationManager permission prompt with our Info.plist purpose
    // string). On web it falls back to navigator.geolocation.
    currentPosition().then(
      async ({ lat, lng }) => {
        const c = [lat, lng];
        setCenter(c);
        setNearby(true);
        const label = await reverseGeocode(c[0], c[1]);
        setAreaLabel(label);
        // Fly the map to the user's actual coords at neighbourhood zoom.
        setFocus({ lat, lng, zoom: 13, label: label || "You are here" });
      },
      async () => {
        // Fallback: UK-wide view with a generous radius so users still see posts.
        const c = [54.5, -2.5];
        setCenter(c);
        setNearby(true);
        setAreaLabel("United Kingdom");
        // No permission → don't fake a fly-to; keep the wide UK view.
        setFocus(null);
      }
    );
  };

  // Debounced forward-geocoding for the search bar. Whenever the user
  // pauses typing for 600ms and the query is ≥3 chars, hit Nominatim to
  // resolve it to lat/lng and fly the map there. Non-blocking: the
  // client-side text filter (title/type/address) still runs as before,
  // so a query like "birmingham" both filters the list AND recentres
  // the map on the city. Clearing the field yields back to auto-fit.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      // Only clear the focus if it came from a previous search — leave
      // the Near-me fly-to intact otherwise.
      if (focus?.source === "search") setFocus(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      const hit = await forwardGeocode(q);
      if (cancelled || !hit) return;
      setFocus({ ...hit, zoom: 12, source: "search" });
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // Intentionally leaving `focus` out of deps: including it would
    // retrigger this effect every time the map flew, causing a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Out-of-coverage detection — only meaningful once the user has explicitly
  // asked for a "Near me" view. Without a real user location we can't sensibly
  // decide they're outside coverage.
  const outOfCoverage = useMemo(() => {
    if (!nearby || !center || coverage.length === 0) return false;
    return coverage.every((c) => distanceKm(center, [c.lat, c.lng]) > c.radius_km);
  }, [nearby, center, coverage]);

  const matchCategory = CATEGORY_CHIPS.find((c) => c.id === categoryFilter)?.match || (() => true);
  const filtered = posts.filter(
    (p) =>
      matchCategory(p) &&
      (!query ||
        p.title.toLowerCase().includes(query.toLowerCase()) ||
        p.waste_type.toLowerCase().includes(query.toLowerCase()) ||
        p.address.toLowerCase().includes(query.toLowerCase()))
  );

  const markers = filtered.map((p) => ({
    id: p.id,
    lat: p.lat,
    lng: p.lng,
    title: p.title,
    subtitle: `${p.waste_type} · ${p.address}`,
    is_demo: p.is_demo,
  }));

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 fade-up">
          <div>
            <Badge className="bg-[#EBE8E0] text-[#1B3624] hover:bg-[#EBE8E0] uppercase text-[10px] tracking-wide">
              Disposal marketplace
            </Badge>
            <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium" style={{ fontFamily: "Outfit" }} data-testid="marketplace-title">
              Disposal
            </h1>
            {carrierArea && (
              <div data-testid="marketplace-carrier-area" className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#6B6965]">
                <Compass className="w-3.5 h-3.5 text-[#1B3624]" />
                Filtered to your service area: <span className="font-medium text-[#1B3624]">{carrierArea.label}</span> · {carrierArea.radius} km
              </div>
            )}
          </div>
          <div className="flex gap-2 items-center w-full md:w-auto flex-wrap md:flex-nowrap">
            <div className="flex p-1 bg-[#EBE8E0] rounded-full shrink-0" data-testid="jobtype-filter">
              {[
                { id: "all", label: "All" },
                { id: "remove", label: "Remove" },
                { id: "clear", label: "Clear" },
              ].map((f) => (
                <button
                  key={f.id}
                  data-testid={`jobtype-${f.id}`}
                  onClick={() => setJobTypeFilter(f.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                    jobTypeFilter === f.id ? "bg-white shadow-sm text-[#1B3624]" : "text-[#6B6965] hover:text-[#1B3624]"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="relative flex-1 md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6965]" />
              <Input
                data-testid="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search removal type, location…"
                className="pl-10 rounded-full bg-white"
              />
            </div>
            {isAdminUser(user) && (
              <div className="hidden md:flex p-1 bg-[#EBE8E0] rounded-full">
                {[
                  { id: "split", icon: <MapIcon className="w-4 h-4" /> },
                  { id: "list", icon: <List className="w-4 h-4" /> },
                ].map((v) => (
                  <button
                    key={v.id}
                    data-testid={`view-${v.id}`}
                    onClick={() => setView(v.id)}
                    className={`p-2 rounded-full ${view === v.id ? "bg-white shadow-sm" : ""}`}
                  >
                    {v.icon}
                  </button>
                ))}
              </div>
            )}
            <Button
              data-testid="nearby-toggle"
              variant={nearby ? "default" : "outline"}
              onClick={() => {
                if (nearby) {
                  setNearby(false);
                  // Also drop any focus that came from Near-me so the
                  // map returns to the auto-fit view.
                  if (focus?.source !== "search") setFocus(null);
                } else {
                  enableNearby();
                }
              }}
              className={`rounded-full ${nearby ? "bg-[#1B3624] hover:bg-[#274d33] text-white" : "border-[#1B3624] text-[#1B3624]"}`}
            >
              <Compass className="w-4 h-4 mr-1" />
              {nearby ? "Near me · on" : "Near me"}
            </Button>
          </div>
        </div>

        {/* Task 6 — horizontal item-level category chips. Overflow-x-auto
            so mobile users can swipe through them; the scrollbar is
            hidden for a cleaner look. */}
        <div
          data-testid="category-chips"
          className="mt-6 -mx-5 sm:mx-0 px-5 sm:px-0 flex gap-2 overflow-x-auto no-scrollbar pb-1"
        >
          {CATEGORY_CHIPS.map((c) => {
            const active = categoryFilter === c.id;
            return (
              <button
                key={c.id}
                type="button"
                data-testid={`category-chip-${c.id}`}
                aria-pressed={active}
                onClick={() => setCategoryFilter(c.id)}
                className={`shrink-0 rounded-full px-4 min-h-[36px] text-xs font-medium tracking-wide border transition-colors ${
                  active
                    ? "bg-[#1B3624] text-white border-[#1B3624]"
                    : "bg-white text-[#1B3624] border-[#E2DFD8] hover:border-[#1B3624]"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        <div className={`mt-6 grid gap-6 ${view === "split" && isAdminUser(user) ? "lg:grid-cols-2" : ""}`}>
          {view !== "list" && isAdminUser(user) && (
            <div className="fade-up delay-1 space-y-3">
              <MapView
                markers={markers}
                coverage={showCoverage ? coverage : []}
                focus={focus}
                height="h-[500px]"
                testId="marketplace-map"
              />
              {/* Coverage legend + toggle. Placed under the map so the map
                  isn't obscured by absolute-positioned overlays on mobile. */}
              <div className="flex flex-wrap items-center justify-between gap-3 px-1">
                <button
                  data-testid="coverage-toggle"
                  onClick={() => setShowCoverage((v) => !v)}
                  className={`inline-flex items-center gap-2 text-xs rounded-full px-3 py-1.5 border transition ${
                    showCoverage
                      ? "bg-[#1B3624] text-white border-[#1B3624]"
                      : "bg-white text-[#1B3624] border-[#E2DFD8] hover:border-[#1B3624]"
                  }`}
                >
                  <span
                    aria-hidden
                    className={`w-2 h-2 rounded-full ${showCoverage ? "bg-[#D67B54]" : "bg-[#B4B1A9]"}`}
                  />
                  {showCoverage ? "Coverage on" : "Show carrier coverage"}
                </button>
                {showCoverage && coverage.length > 0 && (
                  <div
                    data-testid="coverage-legend"
                    className="flex items-center gap-4 text-[11px] text-[#6B6965]"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-full border border-[#4A6B56]" style={{ background: "rgba(74,107,86,0.24)" }} />
                      Carrier service area
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-full border border-dashed border-[#D67B54]" style={{ background: "rgba(214,123,84,0.16)" }} />
                      Demo carrier
                    </span>
                    <span className="text-[#1B3624] font-medium">{coverage.length} live area{coverage.length === 1 ? "" : "s"}</span>
                  </div>
                )}
              </div>
              {outOfCoverage && (
                <WaitingListCallout
                  areaLabel={areaLabel}
                  lat={center?.[0]}
                  lng={center?.[1]}
                />
              )}
            </div>
          )}
          <div className={`grid gap-4 sm:grid-cols-2 ${effectiveView === "split" ? "lg:grid-cols-1 xl:grid-cols-2 lg:max-h-[700px] lg:overflow-y-auto lg:pr-2" : "lg:grid-cols-3"}`}>
            {filtered.length === 0 && (
              <Card className="p-10 text-center rounded-2xl border-dashed border-[#E2DFD8] bg-transparent text-[#6B6965] sm:col-span-2 lg:col-span-3">
                No removal jobs match your search.
              </Card>
            )}
            {filtered.map((p) => {
              const heroImg = p.image_url || (Array.isArray(p.image_urls) && p.image_urls[0]) || null;
              return (
              <Card
                key={p.id}
                role="button"
                tabIndex={0}
                data-testid={`marketplace-post-${p.id}`}
                onClick={() => navigate(`/waste/${p.id}`)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/waste/${p.id}`); } }}
                className="group overflow-hidden rounded-2xl border-[#E2DFD8] bg-white hover:border-[#4A6B56] hover:shadow-md transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#4A6B56] flex flex-col"
              >
                {/* Photo header — 16:9 hero. Falls back to a muted placeholder. */}
                <div
                  data-testid={`marketplace-post-hero-${p.id}`}
                  className="relative w-full bg-[#EBE8E0] overflow-hidden shrink-0"
                  style={{ aspectRatio: "16 / 9" }}
                >
                  {heroImg ? (
                    <img
                      src={heroImg}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-[#9B9893]">
                      <ImageIcon className="w-8 h-8" />
                      <span className="mt-1 text-[11px] uppercase tracking-wider">No photo</span>
                    </div>
                  )}
                  {/* Price / quotes badge overlaid top-right */}
                  <div className="absolute top-3 right-3">
                    {p.bid_count > 0 && p.lowest_bid != null ? (
                      <div
                        data-testid={`price-badge-${p.id}`}
                        className="rounded-full bg-[#1B3624] text-white px-3 py-1.5 text-sm font-medium shadow-sm"
                        style={{ fontFamily: "Outfit" }}
                      >
                        From £{Number(p.lowest_bid).toFixed(0)}
                        <span className="ml-1 text-[10px] font-normal text-white/70 uppercase tracking-wider">· {p.bid_count} bid{p.bid_count === 1 ? "" : "s"}</span>
                      </div>
                    ) : (
                      <div
                        data-testid={`price-badge-${p.id}`}
                        className="rounded-full bg-[#D67B54] text-white px-3 py-1.5 text-xs font-medium uppercase tracking-wider shadow-sm"
                      >
                        Awaiting quotes
                      </div>
                    )}
                  </div>
                  {/* Job type + demo pills overlaid top-left */}
                  <div className="absolute top-3 left-3 flex items-center gap-1.5">
                    {p.job_type === "clear" && (
                      <Badge
                        data-testid={`clear-badge-${p.id}`}
                        className="bg-white/95 text-[#1B3624] hover:bg-white uppercase text-[10px] tracking-wider shadow-sm"
                      >
                        Clear
                      </Badge>
                    )}
                    {p.is_demo && (
                      <Badge
                        data-testid={`demo-badge-${p.id}`}
                        className="bg-[#D67B54]/95 text-white hover:bg-[#D67B54] uppercase text-[10px] tracking-wider shadow-sm"
                      >
                        Demo
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Card body */}
                <div className="flex-1 flex flex-col p-4">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <Badge className="bg-[#EBE8E0] text-[#1B3624] hover:bg-[#EBE8E0] uppercase text-[10px]">
                      {p.waste_type}
                    </Badge>
                    <span className="text-[11px] text-[#6B6965] uppercase tracking-wider">{p.volume}</span>
                  </div>
                  {p.load_size && LOAD_SIZE_LABEL[p.load_size] && (
                    <Badge
                      data-testid={`load-size-badge-${p.id}`}
                      className="mt-2 self-start bg-[#4A6B56]/10 text-[#1B3624] hover:bg-[#4A6B56]/10 uppercase text-[10px] tracking-wider border border-[#4A6B56]/25"
                    >
                      {LOAD_SIZE_LABEL[p.load_size]}
                    </Badge>
                  )}
                  <div className="mt-2 font-medium truncate" style={{ fontFamily: "Outfit" }}>
                    {p.title}
                  </div>
                  <div className="text-xs text-[#6B6965] mt-1 flex items-center gap-1 truncate">
                    <MapPin className="w-3 h-3 shrink-0" /> {p.address}
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[11px] text-[#6B6965]">
                    <span className="truncate">by {p.user_name}{p.distance_km != null ? ` · ${p.distance_km} km` : ""}</span>
                    <span data-testid={`posted-ago-${p.id}`} className="inline-flex items-center gap-1 shrink-0">
                      <Clock className="w-3 h-3" /> Posted {timeAgo(p.created_at, { compact: true })}
                    </span>
                  </div>
                </div>
              </Card>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}
